import { useMutation, useQuery } from "@apollo/client";
import { Checkbox, DatePicker, Drawer, Form, Input, message, Select, TimePicker } from "antd";
import dayjs from "dayjs";
import { isFunction, isString } from "lodash";
import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CREATE_LEAD_TASK, UPDATE_LEAD_TASK } from "../../../graphql/mutation";
import { GET_COMPANY_WORK_HOURS } from "../../../graphql/query";
import { getMobileView } from "../../../redux/slices/isMobile";
import UserSelector from "../../commonComponents/userSelector";
import CustomTimePicker from "../../commonComponents/customTimePicker";
import { UserContext } from "../../../amplify/authenticator-provider";
import { taskStatus } from "./engagementHelpers";
import { addTask, updateLeadTaskRedux } from "../../../redux/slices/taskSlice";
import {
  AccessPermissionAction,
  TENTATIVE_TIME_VALUE,
  timezones,
} from "../../../library/constants";
import { checkAccessPermission } from "../../../library/helpers";
import SuggestedAction from "../tasks/SuggestedAction";
import TaskDrawerHeader from "../../commonComponents/TaskDrawerHeader";
import TaskDrawerFooter from "../../commonComponents/TaskDrawerFooter";
import useLeadRedirection from "../../../hooks/useLeadRedirection";
import { formatDueDateTime, TASK_STATUS } from "../../../constant/TaskConstant";
import { DATE_TIME_FORMATS } from "../../../utils/dateTimes";
import {
  getOpportunityDisplayName,
  getDefaultOpportunity,
  prepareOpportunityList,
} from "../../../helpers/opportunityHelper";
import {
  getRootOppTypeId,
  getUserAllowedRootOppTypeIds,
  isUserUnscoped,
} from "../../../library/oppTypeScoping";
import { calculateWorkingHoursWindow, isNonWorkingDay } from "../../../helpers/appointmentHelpers";

/**
 *
 * @param {{
 * leadTaskData:{
 * open: Boolean,
 * leadTask: any,
 * selectedLead: any,
 * mode: "ADD" | "EDIT"
 * isCompleted:Boolean
 * },
 * setleadTaskData:()=>any,
 * refetchLeadTask:()=>any,
 * setLeadTasks:()=>any,
 * }} props
 * @returns
 */
export const TaskDrawer = props => {
  const dispatch = useDispatch();
  const canEditTask = checkAccessPermission(AccessPermissionAction.TASK_MANAGEMENT_ADD_EDIT);

  const { user } = useContext(UserContext);
  const { redirectToLead } = useLeadRedirection();
  const { leadTaskData, setleadTaskData, selectedOppTypeId } = props;
  const [markedCompleted, setmarkedCompleted] = useState(false);
  const [formChanged, setformChanged] = useState(false);
  const { selectedLead } = useSelector(state => state.lead);
  const [form] = Form.useForm();
  const isMobileView = useSelector(state => getMobileView(state));
  const [assignedUsers, setassignedUsers] = useState(null);

  const getLocation = () => {
    return user?.locations?.find(
      item => item.location.id === sessionStorage.getItem("currentLocationId")
    );
  };
  const locationObj = useMemo(getLocation, [user?.locations]);

  const timeZone = sessionStorage.getItem("currentLocationId")?.includes(",")
    ? props?.selectedLead?.location?.timezone
    : locationObj?.location?.timezone;

  const getTimezoneLabel = () => {
    const obj = timezones.find(item => item.value === timeZone);
    if (obj) {
      return obj.label;
    }
    return timeZone;
  };
  // const timeZoneLabel = useMemo(() => getTimezoneLabel(), timeZone);
  const timeZoneLabel = timeZone;

  const crmIntegrationType = user?.company?.crmIntegrationType;

  // Watch the form's selected due date so workingTime / time-picker stay in sync
  const watchedDueDate = Form.useWatch("dueDate", form);

  const { data: respWorkHrs, loading: workHoursLoading } = useQuery(GET_COMPANY_WORK_HOURS, {
    variables: {
      companyId: user?.company?.id || 0,
      locationId: parseInt(sessionStorage.getItem("currentLocationId") || 0),
    },
    fetchPolicy: "network-only",
    skip: !user?.company?.id,
  });
  const worksHours = respWorkHrs?.getCompanyWorkingHours || [];

  const workingTime = useMemo(
    () =>
      calculateWorkingHoursWindow(
        watchedDueDate,
        worksHours,
        locationObj?.location?.settings?.APPOINTMENT_CREATE_OFFSET
      ),
    [watchedDueDate, worksHours, locationObj]
  );

  const opportunities = useMemo(() => {
    const leadOpportunities = leadTaskData?.selectedLead?.leadOpportunities;
    if (!leadOpportunities || leadOpportunities.length === 0) return [];
    // Filter by user permission first (hide roots the user can't see),
    // then by the active tab's root if one is selected. This keeps the
    // dropdown aligned with the right-side tab strip and ensures the
    // user can never pick an opp they don't have permission for.
    const userAllowed = getUserAllowedRootOppTypeIds(user);
    const unscoped = isUserUnscoped(user);
    const permittedOpps = leadOpportunities.filter(opp => {
      if (unscoped) return true;
      const rootId = getRootOppTypeId(opp?.opportunityType);
      if (!rootId) return true;
      return userAllowed.has(rootId);
    });
    const scopedOpps = selectedOppTypeId
      ? permittedOpps.filter(
          opp => Number(getRootOppTypeId(opp?.opportunityType)) === Number(selectedOppTypeId)
        )
      : permittedOpps;
    return prepareOpportunityList(scopedOpps, crmIntegrationType);
  }, [leadTaskData?.selectedLead?.leadOpportunities, crmIntegrationType, selectedOppTypeId, user]);

  // Auto-select default opportunity in ADD mode
  useEffect(() => {
    if (leadTaskData.mode === "ADD" && leadTaskData.open && opportunities.length > 0) {
      const defaultOpp = getDefaultOpportunity(opportunities);
      if (defaultOpp) {
        form.setFieldsValue({ leadOpportunityId: +defaultOpp.id });
      }
    }
  }, [opportunities, leadTaskData.mode, leadTaskData.open, form]);

  // Auto-assign current user in ADD mode only if user exists in the fetched user list
  const handleUsersLoaded = useCallback(
    fetchedUsers => {
      if (leadTaskData.mode === "ADD" && leadTaskData.open && user?.id) {
        const exists = fetchedUsers.some(u => +u.id === +user.id);
        if (exists) {
          setassignedUsers(+user.id);
          form.setFieldsValue({ assignedTo: +user.id });
        }
      }
    },
    [leadTaskData.mode, leadTaskData.open, user?.id, form]
  );

  const headerProps = useMemo(
    () => ({
      title: leadTaskData.mode === "ADD" ? "Add task" : "Edit task",
      leadData: {
        fullName: leadTaskData?.selectedLead?.fullName,
        id: leadTaskData?.selectedLead?.id,
      },
      onLinkClick: () =>
        redirectToLead(leadTaskData?.selectedLead?.id, {
          loc: leadTaskData?.selectedLead?.locationId,
        }),
      showCloseButton: false,
    }),
    [
      leadTaskData.mode,
      leadTaskData?.selectedLead?.fullName,
      leadTaskData?.selectedLead?.id,
      leadTaskData?.selectedLead?.locationId,
    ]
  );

  useEffect(() => {
    if (leadTaskData.mode === "EDIT" && leadTaskData.open) {
      const { completionDate, completionNote, ...leadTask } = leadTaskData.leadTask;
      form.setFieldsValue({
        ...leadTask,
        dueDate: dayjs(leadTask.dueDate),
        assignedTo: +leadTask.assignedTo.id,
        dueTime: dayjs(leadTask.dueDate).format(DATE_TIME_FORMATS.h_mm_A),
        ...(leadTask.taskStatus === "COMPLETED"
          ? {
              completionNote,
              completionDate: dayjs(completionDate),
              completionTime: dayjs(completionDate),
            }
          : {}),
      });
      setassignedUsers(+leadTaskData.leadTask.assignedTo.id);
      setmarkedCompleted(
        leadTaskData.leadTask.taskStatus === "COMPLETED" || leadTaskData.isCompleted
      );
    }
  }, [leadTaskData]);
  const [createLeadTask, { loading: createTaskLoader }] = useMutation(CREATE_LEAD_TASK);
  const [updateLeadTask, { loading: updateTaskLoader }] = useMutation(UPDATE_LEAD_TASK);

  const handleClose = () => {
    setleadTaskData({ ...leadTaskData, open: false });
    form.resetFields();
    setmarkedCompleted(false);
    setformChanged(false);
    setassignedUsers(null);
  };

  const discardDraft = () => {
    setleadTaskData({ ...leadTaskData, open: false });
    form.resetFields();
    setmarkedCompleted(false);
    setformChanged(false);
  };
  useEffect(() => {
    discardDraft();
  }, [selectedLead.id]);

  const handleSave = async values => {
    // CustomTimePicker returns a string like "10:30 AM"; convert it to dayjs for formatDueDateTime.
    const dueTimeAsDayjs = isString(values.dueTime)
      ? dayjs(values.dueTime, DATE_TIME_FORMATS.h_mm_A)
      : values.dueTime;
    values = {
      ...values,
      assignedTo: assignedUsers,
      dueDate: formatDueDateTime(values.dueDate, dueTimeAsDayjs),
      ...(markedCompleted
        ? { completionDate: formatDueDateTime(values.completionDate, values.completionTime) }
        : {}),
      leadId: leadTaskData.selectedLead.id,
      ...(markedCompleted ? { taskStatus: "COMPLETED" } : { taskStatus: "CREATED" }),
    };
    const { dueTime, ...payload } = values;
    if (leadTaskData.mode === "ADD") {
      const res = await createLeadTask({
        variables: { ...payload },
      });
      if (res.data.createLeadTask.statusCode === 200) {
        message.success(res.data.createLeadTask.message);
        dispatch(addTask(res.data.createLeadTask.leadTask));
        handleClose();
      }
    } else {
      const res = await updateLeadTask({
        variables: {
          ...payload,
          assignedTo: assignedUsers,
          id: +leadTaskData.leadTask.id,
        },
      });
      if (res.data.updateLeadTask.statusCode === 200) {
        message.success(res.data.updateLeadTask.message);
        dispatch(updateLeadTaskRedux(res.data.updateLeadTask.leadTask));
        handleClose();
        if (props?.handleRefetch && isFunction(props?.handleRefetch)) {
          props?.handleRefetch();
        }
      }
    }
  };

  return (
    <Drawer
      width={isMobileView ? "100%" : "600"}
      open={leadTaskData.open}
      title={<TaskDrawerHeader {...headerProps} />}
      footer={
        <TaskDrawerFooter
          onSave={() => form.submit()}
          onCancel={handleClose}
          onDiscardDraft={discardDraft}
          showDiscardDraft={formChanged}
          loading={createTaskLoader || updateTaskLoader}
          disabled={!canEditTask}
          metadata={
            leadTaskData.mode === "EDIT"
              ? {
                  createdBy: leadTaskData.leadTask.createdBy.fullName,
                  completedBy:
                    leadTaskData.leadTask.taskStatus === "COMPLETED"
                      ? leadTaskData.leadTask.completedBy.fullName
                      : null,
                }
              : null
          }
        />
      }
      onClose={handleClose}
    >
      <Form
        onFieldsChange={() => setformChanged(true)}
        onValuesChange={changedValues => {
          // Clear the time selection whenever the user changes the due date.
          if ("dueDate" in changedValues) {
            form.setFieldsValue({ dueTime: undefined });
          }
        }}
        form={form}
        onFinish={handleSave}
        layout="vertical"
        className="[&_.ant-form-item-label]:pb-0"
      >
        {leadTaskData.mode === "EDIT" && (
          <MarkComplete markedCompleted={markedCompleted} setmarkedCompleted={setmarkedCompleted} />
        )}
        <div className="flex-row-centered my-6 w-full items-center gap-2">
          <Form.Item
            label={<span className="form-field-label">Task Type</span>}
            rules={[{ required: true, message: "Please select the task type" }]}
            name="taskType"
            className="w-1/2"
          >
            <Select
              name="taskType"
              placeholder="Select Task Type"
              options={taskStatus}
              className="w-full"
            />
          </Form.Item>
          <Form.Item
            className="w-1/2"
            name="leadOpportunityId"
            rules={[{ required: true, message: "Please select an opportunity" }]}
            label={<span className="form-field-label">Opportunity</span>}
          >
            <Select
              placeholder="Select an opportunity"
              className="w-full"
              name="leadOpportunityId"
              options={
                opportunities &&
                opportunities.length > 0 &&
                opportunities.map(item => ({
                  label: getOpportunityDisplayName(item),
                  value: +item.id,
                }))
              }
              dropdownStyle={{
                maxWidth: "400px",
                minWidth: "400px",
              }}
            />
          </Form.Item>
        </div>
        <Form.Item
          rules={[{ required: true, message: "Please enter task title", whitespace: true }]}
          label={<span className="form-field-label">Title</span>}
          className="w-full mb-6"
          name="title"
        >
          <Input name="title" placeholder="Enter task title" className="w-full" />
        </Form.Item>
        <Form.Item
          rules={[{ required: true, message: "Please enter task description", whitespace: true }]}
          label={<span className="form-field-label">Description</span>}
          className="w-full mb-6"
          name="description"
        >
          <Input.TextArea
            className="w-full"
            placeholder="Enter task description"
            name="description"
          />
        </Form.Item>

        <UserSelector
          multiple={false}
          showAll={false}
          validation
          // item={{ name: "Assigned To" }}
          item={{ name: "Assigned To", item: { user: leadTaskData?.leadTask?.assignedTo } }}
          errorMessage="Please assign this task"
          name="assignedTo"
          handleUsersSelection={value => setassignedUsers(value)}
          onUsersLoaded={handleUsersLoaded}
          ignoreCache
          disabled={leadTaskData?.leadTask?.taskStatus === TASK_STATUS.COMPLETED}
          extraQueryParams={{
            isAllLocationUsers: true,
          }}
        />
        <div className="flex-row-centered mt-6 w-full items-center gap-2">
          <Form.Item
            rules={[{ required: true, message: "Please select the task due date" }]}
            label={<span className="form-field-label">Due Date</span>}
            name="dueDate"
            className="w-full"
          >
            <DatePicker
              disabledDate={date =>
                date.isBefore(dayjs(), "day") || isNonWorkingDay(date, worksHours)
              }
              format="MM/DD/YYYY"
              name="dueDate"
              className="w-full"
            />
          </Form.Item>
          <Form.Item
            name="dueTime"
            label={<span className="form-field-label">Due Time</span>}
            className="w-full"
            rules={[
              { required: true, message: "Please select the task due time" },
              () => ({
                validator(_, value) {
                  if (!value || value === TENTATIVE_TIME_VALUE) return Promise.resolve();
                  const currentDueDate = form.getFieldValue("dueDate");
                  if (!currentDueDate) return Promise.resolve();
                  const now = dayjs();
                  if (!dayjs(currentDueDate).isSame(now, "day")) return Promise.resolve();
                  const selectedDateTime = dayjs(
                    `${dayjs(currentDueDate).format(DATE_TIME_FORMATS.YYYY_MM_DD)} ${value}`,
                    DATE_TIME_FORMATS.YYYY_MM_DD_h_mm_A
                  );
                  return selectedDateTime.isAfter(now)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Please select a future time"));
                },
              }),
            ]}
          >
            <CustomTimePicker
              fkey="due_time"
              startTime={workingTime && workingTime[0]}
              endTime={workingTime && workingTime[1]}
              placeholder="Select time"
              disabled={!watchedDueDate || workHoursLoading}
              defaultDate={watchedDueDate || dayjs()}
            />
          </Form.Item>
        </div>
        <p className="mt-1">
          Current timezone : <b>{timeZoneLabel}</b>
        </p>

        {leadTaskData?.mode !== "ADD" && (
          <div className="mt-5 w-48">
            <SuggestedAction
              suggestedActionItemClick={() =>
                redirectToLead(leadTaskData?.selectedLead?.id, {
                  loc: leadTaskData?.selectedLead?.locationId,
                })
              }
            />
          </div>
        )}
        {/* <Form.Item className="w-full" label="Reminder">
          <Select className="w-full" />
        </Form.Item> */}
      </Form>
    </Drawer>
  );
};

const MarkComplete = ({ markedCompleted, setmarkedCompleted }) => {
  return (
    <div className="p-3 mb-3 rounded-lg border border-solid border-success-300 bg-success-50">
      <Checkbox checked={markedCompleted} onChange={e => setmarkedCompleted(e.target.checked)}>
        Mark as complete
      </Checkbox>
      {markedCompleted && (
        <div className="flex-col-centered mt-6 gap-5">
          <div className="flex-row-centered w-full items-center gap-2">
            <Form.Item
              rules={[{ required: true, message: "Please select the task completion date" }]}
              label={<span className="form-field-label">Completion Date</span>}
              name="completionDate"
              className="w-full"
            >
              <DatePicker
                disabledDate={date => date.isAfter(dayjs())}
                name="completionDate"
                className="w-full"
                format="MM/DD/YYYY"
              />
            </Form.Item>
            <Form.Item
              name="completionTime"
              label={<span className="form-field-label">Completion Time</span>}
              className="w-full"
              rules={[{ required: true, message: "Please select the task completion time" }]}
            >
              <TimePicker format="hh:mm A" name="completionTime" className="w-full" />
            </Form.Item>
          </div>
          <Form.Item
            name="completionNote"
            label={<span className="form-field-label">Completion Note</span>}
            rules={[
              { required: true, message: "Please enter the completion note", whitespace: true },
            ]}
          >
            <Input.TextArea placeholder="Enter the completion note" name="completionNote" />
          </Form.Item>
        </div>
      )}
    </div>
  );
};
